/**
 * Button (adapter) → renders the app's ONE button: Primary_Button.
 * ──────────────────────────────────────────────────────────────────────────
 * The whole app imports `Button` from the components barrel. This adapter keeps
 * that call-site API (kind / size / icon-string / iconOnly / block …) but
 * renders ONLY `Primary_Button` underneath — so every button in the entire
 * application is your Primary_Button. The old standalone Button component has
 * been removed; this is the single source of truth for buttons.
 */

import React from 'react';
import { StyleProp, ViewStyle, TextStyle } from 'react-native';
import PrimaryButton, { ButtonVariant } from './primaryButton';
import Icon from './icon';
import { useTheme } from '../context/themeContext';

// Call-site API (matches the button props used across the app).
export type ButtonKind = 'primary' | 'accent' | 'danger' | 'ghost' | 'outline' | 'default';
export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg';

interface ButtonProps {
  kind?: ButtonKind;
  size?: ButtonSize;
  icon?: string | React.ReactNode;
  iconRight?: boolean;
  loading?: boolean;
  block?: boolean;
  iconOnly?: boolean;
  disabled?: boolean;
  onPress?: () => void;
  children?: React.ReactNode;
  style?: StyleProp<ViewStyle>;
  textStyle?: StyleProp<TextStyle>;
  testID?: string;
  hoverLift?: boolean;
  'aria-label'?: string;
}

// kind → Primary_Button variant (accent is this app's indigo primary).
const KIND_TO_VARIANT: Record<ButtonKind, ButtonVariant> = {
  primary: 'primary',
  accent: 'primary',
  danger: 'danger',
  ghost: 'ghost',
  outline: 'outline',
  default: 'outline',
};

// Icon tint per variant, matched to Primary_Button's text colors. Resolved from
// theme so glyphs invert with the rest of the button in dark mode.
function iconColorFor(variant: ButtonVariant, colors: any): string {
  switch (variant) {
    case 'danger':
      // Danger fill is light in dark mode → glyph must be dark to stay visible.
      return colors.onAccent;
    case 'google':
      return colors.white;
    case 'dangerSoft':
    case 'cancel':
      return colors.danger;
    case 'outline':
      return colors.ink2;
    case 'ghost':
    default:
      return colors.ink2;
  }
}

export default function Button({
  kind = 'default',
  size = 'md',
  icon,
  iconRight = false,
  loading = false,
  block = false,
  iconOnly = false,
  disabled = false,
  onPress,
  children,
  style,
  textStyle,
  testID,
  hoverLift = false,
}: ButtonProps) {
  const { theme } = useTheme();
  const { colors } = theme;

  // Icon-only delete buttons get the premium soft-danger treatment (calm red
  // tint + hover lift); full danger buttons (destructive CTAs with a label)
  // keep the bold solid red.
  const variant =
    iconOnly && kind === 'danger' ? 'dangerSoft' : (KIND_TO_VARIANT[kind] ?? 'outline');
  // One standard, compact size across the whole app — the `size` prop is kept
  // for call-site compatibility but no longer changes the rendered height.
  const pbSize = 'small' as const;
  const isLarge = false;

  // App-wide premium treatment for the primary/accent (filled) button: a
  // theme-adaptive ink background with paper-coloured text, so it reads as a
  // dark button in light mode and inverts cleanly in dark mode. Hover lifts.
  const isPrimary = variant === 'primary';

  // String icon name → Icon node; an already-built node passes through as-is.
  // Primary/filled button: a bold DARK pill (ink bg + paper text) in light mode,
  // and a subtle bordered surface (card bg + light text) in dark mode.
  const primaryFg = theme.isDark ? colors.ink2 : colors.paper;
  const iconColor = isPrimary ? primaryFg : iconColorFor(variant, colors);
  const iconNode =
    icon == null
      ? undefined
      : typeof icon === 'string'
        ? <Icon name={icon} size={isLarge ? 18 : 16} color={iconColor} />
        // A passed icon node is re-colored to the themed icon color so every
        // button's glyph adapts (call sites can hardcode any color; we override).
        : React.isValidElement(icon)
          ? React.cloneElement(icon as React.ReactElement<any>, { color: iconColor })
          : icon;

  // Title: icon-only buttons have none; otherwise coerce the (string) child.
  const title = iconOnly
    ? ''
    : typeof children === 'string'
      ? children
      : children == null
        ? ''
        : String(children);

  // Theme-adaptive container colors per variant. These are injected via the
  // inline `style`/`textStyle` so they win over Primary_Button's static
  // StyleSheet variant colors, keeping every button adaptive in dark/light.
  let variantContainer: ViewStyle | null = null;
  let variantTextColor: string | null = null;
  switch (variant) {
    case 'primary':
      // Light mode → bold dark pill (ink bg, paper/white text).
      // Dark mode  → subtle bordered surface (card bg, light text + border).
      variantContainer = theme.isDark
        ? {
            backgroundColor: colors.card,
            borderColor: colors.lineStrong,
            borderRadius: 10,
            ...(typeof window !== 'undefined' ? { boxShadow: '0 1px 2px rgba(0,0,0,0.18)', transition: 'all 160ms ease' } as any : {}),
          }
        : {
            backgroundColor: colors.ink,
            borderColor: colors.ink,
            borderRadius: 10,
            ...(typeof window !== 'undefined' ? { boxShadow: '0 1px 2px rgba(0,0,0,0.18)', transition: 'all 160ms ease' } as any : {}),
          };
      variantTextColor = primaryFg;
      break;
    case 'outline':
    case 'cancel':
      // outline/default: transparent bg, themed border, secondary ink text.
      variantContainer = {
        backgroundColor: colors.transparent,
        borderColor: variant === 'cancel' ? colors.dangerLine : colors.line,
      };
      variantTextColor = variant === 'cancel' ? colors.danger : colors.ink2;
      break;
    case 'ghost':
      variantContainer = { backgroundColor: colors.transparent };
      variantTextColor = colors.ink2;
      break;
    case 'danger':
      // Destructive CTA: solid danger block; text uses onAccent so it stays
      // legible when the danger fill is light in dark mode.
      variantContainer = { backgroundColor: colors.danger, borderColor: colors.danger };
      variantTextColor = colors.onAccent;
      break;
    case 'dangerSoft':
      // Icon-delete soft treatment, themed so it inverts in dark mode.
      variantContainer = { backgroundColor: colors.dangerSoft, borderColor: colors.dangerLine };
      variantTextColor = colors.danger;
      break;
    default:
      break;
  }

  // Disabled state uses a muted theme token, not hardcoded slate.
  const disabledContainer: ViewStyle | null = disabled
    ? { backgroundColor: colors.line, borderColor: colors.line }
    : null;
  const disabledTextColor = disabled ? colors.ink4 : null;

  // Old Button is inline by default; `block` makes it full-width.
  const mergedStyle: StyleProp<ViewStyle> = [
    { alignSelf: block ? 'stretch' : 'flex-start' },
    // Soft-danger keeps its premium rounding (the `small` size otherwise resets it).
    variant === 'dangerSoft' ? { borderRadius: 8 } : null,
    // Theme-adaptive variant container (applies app-wide).
    variantContainer,
    style as ViewStyle,
    // Disabled wins last so it always reads as muted.
    disabledContainer,
  ];

  const mergedTextStyle: StyleProp<TextStyle> = [
    variantTextColor ? { color: variantTextColor } : null,
    textStyle,
    disabledTextColor ? { color: disabledTextColor } : null,
  ];

  return (
    <PrimaryButton
      title={title}
      variant={variant}
      size={pbSize}
      onPress={onPress ?? (() => {})}
      disabled={disabled}
      loading={loading}
      leftIcon={!iconRight ? iconNode : undefined}
      rightIcon={iconRight ? iconNode : undefined}
      style={mergedStyle}
      textStyle={mergedTextStyle}
      testID={testID}
      hoverLift={hoverLift || isPrimary}
    />
  );
}
